import { useState, useEffect, useRef } from 'react';
import SpendingSummary from '../components/SpendingSummary';
import TotalBalanceCard from '../components/TotalBalanceCard';
import AccountsDisplayCard from '../components/AccountsDisplayCard';
import MonthlyCashFlowCard from '../components/MonthlyCashFlowCard';
import CustomToast from '../components/CustomToast';
import { useNavigation } from '../hooks/useNavigation';
import { useRouteScrollContainer } from '../hooks/useRouteScrollContainer';
import { LuSettings2 } from 'react-icons/lu';
import { FiMail } from 'react-icons/fi';

function HomeScreen() {
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [isToastVisible, setIsToastVisible] = useState<boolean>(false);
  const toastTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const { navigateTo } = useNavigation();
  const setRouteScrollContainer = useRouteScrollContainer<HTMLDivElement>('screen:home');

  useEffect(() => {
    // Clear timeout on unmount
    return () => {
      if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
    };
  }, []);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const error = urlParams.get('google_auth_error');
    const success = urlParams.get('google_auth_success');

    let message: string | null = null;

    if (error) {
      console.error('Google Auth Error:', error);
      message = `Google Authentication failed: ${error}`;
    }
    if (success) {
      console.log('Google Auth Success!');
      message = 'Google account connected successfully!';
    }

    if (message) {
      setToastMessage(message);
      setIsToastVisible(true);
      // Clean the URL
      window.history.replaceState({}, document.title, window.location.pathname);

      // Clear existing timeout if any
      if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
      // Set new timeout for the toast
      toastTimeoutRef.current = setTimeout(() => {
        setIsToastVisible(false);
        setToastMessage(null);
      }, 5000);
    }
  }, []);

  return <div ref={setRouteScrollContainer} className='relative h-full flex flex-col overflow-y-auto enable-scroll'>
    <div className="bg-background text-foreground h-auto flex flex-col" style={{ scrollbarWidth: 'none' }}>
      <div className="flex flex-shrink-0 justify-between items-center px-5 py-2.5">
        <h1 className="text-3xl font-bold">Home</h1>
        <div className="flex items-center gap-3">
          <button
            className="text-foreground hover:text-foreground transition-colors"
            aria-label="Emails"
            onClick={() => navigateTo('/emails')}
          >
            <FiMail className='w-6 h-6' />
          </button>
          <button
            className="text-foreground hover:text-foreground transition-colors"
            aria-label="Settings"
            onClick={() => navigateTo('/settings')}
          >
            <LuSettings2 className='w-6 h-6' />
          </button>
        </div>
      </div>

      <div className='h-auto p-4 pt-2'>
        <div className="columns-1 gap-4 md:columns-2 lg:columns-3 xl:columns-4">

          <div className="mb-4 inline-block w-full break-inside-avoid align-top">
            <TotalBalanceCard />
          </div>

          <div className="mb-4 inline-block w-full break-inside-avoid align-top">
            <MonthlyCashFlowCard />
          </div>

          <div className="mb-4 inline-block w-full break-inside-avoid align-top">
            <SpendingSummary />
          </div>

          <div className="mb-4 inline-block w-full break-inside-avoid align-top">
            <AccountsDisplayCard
              title="Credit Cards"
              accountTypes={['CREDIT_CARD']}
              emptyStateMessage="No credit cards found"
            />
          </div>

          <div className="mb-4 inline-block w-full break-inside-avoid align-top">
            <AccountsDisplayCard
              title="Bank Accounts"
              accountTypes={['SAVINGS', 'DEBIT_CARD']}
              emptyStateMessage="No bank accounts found"
            />
          </div>

        </div>
      </div>
    </div>

    <CustomToast message={toastMessage} isVisible={isToastVisible} />
  </div>
}

export default HomeScreen; 
